import { Link } from "react-router-dom";
import { Check, Minus, ArrowRight, Info } from "lucide-react";
import SiteHeader from "@/components/site/SiteHeader";
import SiteFooter from "@/components/site/SiteFooter";
import { Reveal, Eyebrow } from "@/lib/motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from "sonner";

const PLANS = [
  {
    name: "Starter",
    desc: "For small businesses getting started",
    popular: false,
    feeRows: [
      ["Monthly platform fee", "₹X,XXX/month"],
      ["Included usage", "X00 call minutes + X,000 messages"],
      ["Additional call usage", "₹X.XX per minute"],
      ["Additional WhatsApp / messaging", "As per standard rates"],
      ["Optional setup fee", "₹X,XXX one-time"],
    ],
  },
  {
    name: "Business",
    desc: "For businesses handling regular customer conversations",
    popular: true,
    feeRows: [
      ["Monthly platform fee", "₹X,XXX/month"],
      ["Included usage", "X,000 call minutes + X0,000 messages"],
      ["Additional call usage", "₹X.XX per minute"],
      ["Additional WhatsApp / messaging", "As per standard rates"],
      ["Optional setup fee", "₹X,XXX one-time"],
    ],
  },
  {
    name: "Custom",
    desc: "For businesses needing custom automation and integrations",
    popular: false,
    feeRows: [
      ["Monthly platform fee", "Custom pricing"],
      ["Included usage", "Based on your needs"],
      ["Additional call usage", "Custom rates"],
      ["Additional WhatsApp / messaging", "Custom rates"],
      ["Optional setup fee", "Discussed with you"],
    ],
  },
];

const TABLE_ROWS = [
  { feature: "AI call agent", starter: true, business: true, custom: true },
  { feature: "WhatsApp", starter: true, business: true, custom: true },
  { feature: "Email", starter: true, business: true, custom: true },
  { feature: "Order and tracking support", starter: true, business: true, custom: true },
  { feature: "Follow-ups", starter: true, business: true, custom: true },
  { feature: "Social media drafts", starter: false, business: true, custom: true },
  { feature: "Automatic social posting", starter: false, business: true, custom: true },
  { feature: "Website integration", starter: false, business: true, custom: true },
  { feature: "Human takeover", starter: true, business: true, custom: true },
  { feature: "Number of team members", starter: "1", business: "Up to 5", custom: "Unlimited" },
  { feature: "Monthly usage", starter: "Basic", business: "High", custom: "Custom" },
];

function Cell({ v }) {
  if (v === true) return <Check className="mx-auto h-5 w-5 text-emerald-600" />;
  if (v === false) return <Minus className="mx-auto h-5 w-5 text-slate-300" />;
  return <span className="text-sm font-medium text-slate-700">{v}</span>;
}

export default function Pricing() {
  return (
    <div className="bg-white" data-testid="pricing-page">
      <SiteHeader />

      <section className="mx-auto max-w-7xl px-4 pb-4 pt-16 text-center sm:px-6 md:pt-24 lg:px-8" data-testid="pricing-hero">
        <Reveal>
          <Eyebrow>Simple, transparent pricing</Eyebrow>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">
            One plan for your <span className="text-orange-600">AI employee</span>
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-base text-slate-600 sm:text-lg">
            No hidden charges. Every plan separates the platform fee from usage, so you always know
            what you are paying for.
          </p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8" data-testid="pricing-plans">
        <div className="grid gap-6 lg:grid-cols-3">
          {PLANS.map((p, i) => (
            <Reveal key={p.name} delay={i * 0.1}>
              <div
                className={`relative flex h-full flex-col rounded-2xl bg-white p-7 md:p-8 ${
                  p.popular ? "border-2 border-orange-500 shadow-xl" : "border border-slate-200"
                }`}
                data-testid={`pricing-card-${p.name.toLowerCase()}`}
              >
                {p.popular && (
                  <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 rounded-full bg-orange-600 px-4 py-1 text-xs font-bold text-white">
                    Most Popular
                  </span>
                )}
                <h2 className="font-display text-xl font-bold text-slate-900">{p.name}</h2>
                <p className="mt-1.5 text-sm text-slate-500">{p.desc}</p>
                <p className="mt-6 font-display text-4xl font-bold text-slate-900">
                  {p.name === "Custom" ? (
                    "Let's talk"
                  ) : (
                    <>₹X,XXX<span className="text-base font-medium text-slate-500">/month</span></>
                  )}
                </p>
                <div className="mt-7 flex-1 space-y-0 divide-y divide-slate-100 rounded-xl border border-slate-100 bg-[#FAFAF7]">
                  {p.feeRows.map(([label, value]) => (
                    <div key={label} className="flex items-center justify-between gap-4 px-4 py-3">
                      <span className="text-xs font-medium text-slate-500 sm:text-sm">{label}</span>
                      <span className="text-right text-xs font-semibold text-slate-900 sm:text-sm">{value}</span>
                    </div>
                  ))}
                </div>
                <button
                  data-testid={`pricing-cta-${p.name.toLowerCase()}`}
                  onClick={() => toast.info("Design preview: checkout opens here in the live product")}
                  className={`mt-7 w-full rounded-xl px-5 py-3.5 text-sm font-semibold transition-colors ${
                    p.popular
                      ? "bg-orange-600 text-white hover:bg-orange-700"
                      : "border-2 border-slate-200 text-slate-800 hover:bg-slate-50"
                  }`}
                >
                  {p.name === "Custom" ? "Talk to us" : "Choose " + p.name}
                </button>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-8">
          <div className="flex items-start gap-3 rounded-2xl border border-orange-200 bg-orange-50 p-5" data-testid="third-party-charges-note">
            <Info className="mt-0.5 h-5 w-5 shrink-0 text-orange-600" />
            <p className="text-sm leading-relaxed text-orange-900">
              <span className="font-semibold">Please note:</span> Third-party charges such as
              WhatsApp, calling, email or other service usage may be billed separately when
              applicable.
            </p>
          </div>
        </Reveal>
      </section>

      <section className="bg-[#FAFAF7] py-16 md:py-20" data-testid="comparison-section">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center">
            <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              Compare plans in detail
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="mt-10">
            <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white scrollbar-thin" data-testid="comparison-table">
              <table className="w-full min-w-[560px] text-left">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/70">
                    <th className="px-5 py-4 text-sm font-semibold text-slate-900">Feature</th>
                    <th className="px-5 py-4 text-center text-sm font-semibold text-slate-900">Starter</th>
                    <th className="px-5 py-4 text-center text-sm font-semibold text-orange-700">Business</th>
                    <th className="px-5 py-4 text-center text-sm font-semibold text-slate-900">Custom</th>
                  </tr>
                </thead>
                <tbody>
                  {TABLE_ROWS.map((r, i) => (
                    <tr key={r.feature} className={i % 2 ? "bg-slate-50/40" : ""} data-testid={`comparison-row-${i}`}>
                      <td className="px-5 py-3.5 text-sm font-medium text-slate-700">{r.feature}</td>
                      <td className="px-5 py-3.5 text-center"><Cell v={r.starter} /></td>
                      <td className="bg-orange-50/40 px-5 py-3.5 text-center"><Cell v={r.business} /></td>
                      <td className="px-5 py-3.5 text-center"><Cell v={r.custom} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-4 py-16 sm:px-6 md:py-20 lg:px-8" data-testid="pricing-faq-section">
        <Reveal className="text-center">
          <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            Pricing questions
          </h2>
        </Reveal>
        <Reveal delay={0.1} className="mt-8">
          <Accordion type="single" collapsible data-testid="pricing-faq-accordion">
            {[
              { q: "Is there a free trial?", a: "You can set up and fully test your AI employee before going live. You only start paying when your AI starts talking to real customers." },
              { q: "What counts as usage?", a: "Usage means call minutes handled by the AI call agent and WhatsApp or other messages sent. Each plan includes a monthly usage amount, and extra usage is charged at the rates shown." },
              { q: "Can I change plans later?", a: "Yes, you can move up or down anytime. Changes apply from the next billing month." },
              { q: "Can I cancel anytime?", a: "Yes. There is no lock-in. See our Refund and Cancellation Policy for details." },
            ].map((f, i) => (
              <AccordionItem key={i} value={`pfaq-${i}`} data-testid={`pricing-faq-item-${i}`}>
                <AccordionTrigger data-testid={`pricing-faq-question-${i}`} className="text-left font-display text-base font-semibold text-slate-900">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-slate-600">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
        <Reveal className="mt-12 text-center">
          <p className="text-sm text-slate-500">Not sure which plan fits your business?</p>
          <button
            data-testid="pricing-talk-to-us-button"
            onClick={() => toast.success("Design preview: our team will contact you")}
            className="mt-4 inline-flex items-center gap-2 rounded-xl bg-slate-900 px-8 py-3.5 text-base font-semibold text-white transition-colors hover:bg-slate-800"
          >
            Talk to us <ArrowRight className="h-4 w-4" />
          </button>
          <p className="mt-6 text-sm text-slate-500">
            <Link to="/" className="font-medium text-orange-700 hover:underline" data-testid="pricing-back-home-link">
              ← Back to homepage
            </Link>
          </p>
        </Reveal>
      </section>

      <SiteFooter />
    </div>
  );
}
